import {
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useRef,
	useState,
	type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
	getFixedMenuCoords,
	type FixedMenuCoords,
} from '@/components/ui/AnchoredDropdownMenu';
import { cn } from '@/lib/utils';
import {
	findProntuarioPanelBoundary,
	getProntuarioPanelAnchoredCoords,
} from '../../utils/getProntuarioPanelAnchoredCoords';

const PANEL_WIDTH = 280;
const PANEL_HEIGHT_ESTIMATE = 220;
const HOVER_CLOSE_DELAY_MS = 120;

const GoogleMaterialInfoIcon = ({ className }: { className?: string }) => (
	<svg
		viewBox="0 0 24 24"
		className={className}
		fill="currentColor"
		aria-hidden
	>
		<path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
	</svg>
);

const FormattingExample = ({
	mark,
	children,
}: {
	mark: string;
	children: ReactNode;
}) => (
	<li className="flex items-center gap-2">
		<code className="shrink-0 rounded-md border border-white/[0.08] bg-[#121A24] px-1.5 py-0.5 font-mono text-[11px] text-slate-200">
			{mark}
		</code>
		<span className="min-w-0 flex-1 text-[13px] leading-relaxed">
			{children}
		</span>
	</li>
);

const FormattingHelpPanel = () => (
	<div>
		<p className="mb-2.5 text-[12px] font-medium tracking-[0.01em] text-slate-200">
			Formatação
		</p>
		<ul className="space-y-2">
			<FormattingExample mark="*texto*">
				<em className="font-normal italic text-slate-200">itálico</em>
			</FormattingExample>
			<FormattingExample mark="**texto**">
				<span className="font-normal text-slate-50 [text-shadow:0.55px_0_0_currentColor,-0.55px_0_0_currentColor]">
					negrito
				</span>
			</FormattingExample>
			<FormattingExample mark="`texto`">
				<span className="font-normal text-emerald-200/90 underline decoration-emerald-400/40 decoration-1 underline-offset-[3px]">
					destaque
				</span>
			</FormattingExample>
			<FormattingExample mark="- item">
				<span className="inline-flex items-center gap-1.5 text-slate-200">
					<span className="text-slate-400">•</span>
					<span>item</span>
				</span>
			</FormattingExample>
		</ul>
		<p className="mt-3 border-t border-white/[0.08] pt-2.5 text-[12px] leading-relaxed text-slate-500">
			Marcadores sem espaços funcionam normalmente (ex.:{' '}
			<code className="text-slate-400">*ok*</code>). Já com espaços (ex.:{' '}
			<code className="text-slate-400">* não *</code>), a formatação não é
			aplicada. Além disso, os asteriscos somem ao sair da edição e
			reaparecem ao editar o texto novamente.
		</p>
	</div>
);

const SessionCallProntuarioNotesFormattingHelp = ({
	className,
}: {
	className?: string;
}) => {
	const [hovered, setHovered] = useState(false);
	const [pinned, setPinned] = useState(false);
	const [coords, setCoords] = useState<FixedMenuCoords | null>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const hideTimeoutRef = useRef<number>();
	const helpId = useId();
	const open = hovered || pinned;

	const updateCoords = useCallback(() => {
		const anchor = triggerRef.current;
		if (!anchor) return;

		const boundary = findProntuarioPanelBoundary(anchor);
		const nextCoords = boundary
			? getProntuarioPanelAnchoredCoords(anchor, boundary, {
					menuWidth: PANEL_WIDTH,
					estimatedHeight: PANEL_HEIGHT_ESTIMATE,
					gap: 6,
			  })
			: getFixedMenuCoords(anchor, {
					menuWidth: PANEL_WIDTH,
					estimatedHeight: PANEL_HEIGHT_ESTIMATE,
					gap: 6,
			  });

		setCoords(nextCoords);
	}, []);

	const clearHideTimeout = () => {
		window.clearTimeout(hideTimeoutRef.current);
	};

	const scheduleHoverClose = () => {
		clearHideTimeout();
		hideTimeoutRef.current = window.setTimeout(() => {
			setHovered(false);
		}, HOVER_CLOSE_DELAY_MS);
	};

	const showFromHover = () => {
		clearHideTimeout();
		setHovered(true);
	};

	useLayoutEffect(() => {
		if (!open) {
			setCoords(null);
			return;
		}

		updateCoords();
	}, [open, updateCoords]);

	useEffect(() => {
		if (!open) return;

		const onLayout = () => updateCoords();
		window.addEventListener('resize', onLayout);
		window.addEventListener('scroll', onLayout, true);

		return () => {
			window.removeEventListener('resize', onLayout);
			window.removeEventListener('scroll', onLayout, true);
		};
	}, [open, updateCoords]);

	useEffect(() => {
		if (!pinned) return;

		const closeOnOutside = (event: PointerEvent) => {
			const trigger = triggerRef.current;
			if (trigger?.contains(event.target as Node)) return;
			setPinned(false);
		};

		document.addEventListener('pointerdown', closeOnOutside, true);
		return () => {
			document.removeEventListener('pointerdown', closeOnOutside, true);
		};
	}, [pinned]);

	useEffect(() => {
		return () => clearHideTimeout();
	}, []);

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				aria-expanded={open}
				aria-describedby={open ? helpId : undefined}
				aria-label="Como formatar as anotações"
				onMouseEnter={showFromHover}
				onMouseLeave={scheduleHoverClose}
				onClick={(event) => {
					event.preventDefault();
					event.stopPropagation();
					setPinned((value) => !value);
				}}
				className={cn(
					'relative -mt-px flex size-7 shrink-0 items-center justify-center rounded-full',
					'text-[#9aa0a6] transition-[color,background-color,transform,box-shadow] duration-200 ease-out',
					'hover:bg-[#8ab4f8]/12 hover:text-[#aecbfa]',
					'active:scale-[0.96] active:bg-[#8ab4f8]/20',
					open &&
						'bg-[#8ab4f8]/16 text-[#aecbfa] shadow-[0_1px_2px_rgba(0,0,0,0.3)]',
					className,
				)}
			>
				<GoogleMaterialInfoIcon className="size-4" />
			</button>

			{open && coords
				? createPortal(
						<div
							id={helpId}
							data-tutorial-demo="prontuario-formatting-help"
							role="tooltip"
							style={{
								top: coords.top,
								left: coords.left,
								width: coords.width ?? PANEL_WIDTH,
								maxWidth: coords.width ?? PANEL_WIDTH,
								transform: coords.transform,
							}}
							className={cn(
								'fixed z-[320]',
								'rounded-xl border border-white/[0.08] bg-[#1a2430] px-4 py-3',
								'shadow-[0_8px_24px_rgba(0,0,0,0.4)]',
							)}
							onMouseEnter={showFromHover}
							onMouseLeave={scheduleHoverClose}
							onClick={(event) => event.stopPropagation()}
						>
							<FormattingHelpPanel />
						</div>,
						document.body,
				  )
				: null}
		</>
	);
};

export { SessionCallProntuarioNotesFormattingHelp };
